"use client";

import type { FC, KeyboardEvent, ReactNode } from "react";
import { useId, useMemo, useRef, useState } from "react";
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import * as RadixPopover from "@radix-ui/react-popover";
import type { TimeOfDay, TimeRange } from "cmw-client";
import { cn } from "@/lib/cn";
import { formatDuration, formatTimeOfDay, normalizeTimeQuery, timeOptions } from "@/lib/time-of-day";
import { FieldLabel, FieldMessage, POPOVER_PANEL_WIDTH, type FieldState } from "./field-chrome";

type TimeOption = { value: TimeOfDay; label: string };

const ALL_TIME_OPTIONS: TimeOption[] = timeOptions();

const matchesQuery = (option: TimeOption, query: string) =>
  normalizeTimeQuery(option.label).includes(normalizeTimeQuery(query));

const scrollIntoView = (container: HTMLElement | null, index: number) => {
  container?.children[index]?.scrollIntoView({ block: "nearest" });
};

type TimeRangePickerProps = {
  id?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value: TimeRange | null;
  onValueChange: (value: TimeRange | null) => void;
  className?: string;
};

export const TimeRangePicker: FC<TimeRangePickerProps> = ({
  id,
  label,
  message,
  state = "default",
  required,
  disabled,
  placeholder = "Select time",
  value,
  onValueChange,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [fromHighlight, setFromHighlight] = useState(0);
  const [toHighlight, setToHighlight] = useState(0);
  const [activeColumn, setActiveColumn] = useState<"from" | "to">("from");
  const [pendingStart, setPendingStart] = useState<TimeOfDay | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const fromListRef = useRef<HTMLDivElement>(null);
  const toListRef = useRef<HTMLDivElement>(null);

  const fromHeadingId = useId();
  const toHeadingId = useId();

  const filteredFrom = useMemo(
    () => ALL_TIME_OPTIONS.filter((option) => matchesQuery(option, search)),
    [search],
  );

  const filteredTo = useMemo(
    () =>
      pendingStart
        ? ALL_TIME_OPTIONS.filter((option) => option.value > pendingStart && matchesQuery(option, search))
        : [],
    [search, pendingStart],
  );

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;

    setSearch("");
    setActiveColumn("from");

    const start = value?.start ?? null;
    setPendingStart(start);

    const fromIndex = start ? Math.max(ALL_TIME_OPTIONS.findIndex((option) => option.value === start), 0) : 0;
    const toOptions = start ? ALL_TIME_OPTIONS.filter((option) => option.value > start) : [];
    const toIndex = value?.end ? Math.max(toOptions.findIndex((option) => option.value === value.end), 0) : 0;

    setFromHighlight(fromIndex);
    setToHighlight(toIndex);

    requestAnimationFrame(() => {
      scrollIntoView(fromListRef.current, fromIndex);
      scrollIntoView(toListRef.current, toIndex);
    });
  };

  const focusToColumn = () => {
    setToHighlight(0);
    setActiveColumn("to");
    requestAnimationFrame(() => {
      if (toListRef.current) toListRef.current.scrollTop = 0;
      toListRef.current?.focus();
    });
  };

  const selectFrom = (start: TimeOfDay) => {
    setPendingStart(start);
    setSearch("");
    const retainedEnd = value?.end && value.end > start ? value.end : null;
    if (retainedEnd) onValueChange({ start, end: retainedEnd });
    focusToColumn();
  };

  const selectTo = (end: TimeOfDay) => {
    if (!pendingStart) return;
    onValueChange({ start: pendingStart, end });
    setOpen(false);
  };

  const clear = () => {
    onValueChange(null);
    setOpen(false);
  };

  const navigate = (column: "from" | "to", key: "ArrowDown" | "ArrowUp" | "Enter") => {
    const options = column === "from" ? filteredFrom : filteredTo;
    const highlight = column === "from" ? fromHighlight : toHighlight;
    const setHighlight = column === "from" ? setFromHighlight : setToHighlight;
    const listRef = column === "from" ? fromListRef : toListRef;

    if (key === "ArrowDown") {
      const next = Math.min(highlight + 1, options.length - 1);
      setHighlight(next);
      scrollIntoView(listRef.current, next);
    } else if (key === "ArrowUp") {
      const next = Math.max(highlight - 1, 0);
      setHighlight(next);
      scrollIntoView(listRef.current, next);
    } else {
      const option = options[highlight];
      if (option) {
        if (column === "from") selectFrom(option.value);
        else selectTo(option.value);
      }
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter") {
      event.preventDefault();
      navigate(activeColumn, event.key);
    }
  };

  const handleColumnKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter") {
      event.preventDefault();
      const column = event.currentTarget === fromListRef.current ? "from" : "to";
      navigate(column, event.key);
    }
  };

  const displayValue =
    value?.start && value?.end ? `${formatTimeOfDay(value.start)} – ${formatTimeOfDay(value.end)}` : null;

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <FieldLabel htmlFor={id} state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RadixPopover.Trigger
          id={id}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            "flex w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-black/5 p-16 text-left outline-none focus:border-brand-blue disabled:cursor-not-allowed",
            state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
          )}
        >
          <span className={cn("min-w-0 truncate text-16", displayValue ? "text-neutral-black" : "text-neutral-black/35")}>
            {displayValue ?? placeholder}
          </span>
          <ChevronDownIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              searchInputRef.current?.focus();
            }}
            className={cn(
              "pointer-events-auto z-50 flex flex-col gap-8 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div className="flex items-center gap-8 rounded-small border border-neutral-mid-grey px-12 py-8">
              <MagnifyingGlassIcon className="size-20 shrink-0 text-neutral-black/35" aria-hidden />
              <input
                ref={searchInputRef}
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setFromHighlight(0);
                  setToHighlight(0);
                }}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search a time…"
                className="min-w-0 flex-1 bg-transparent text-16 text-neutral-black outline-none placeholder:text-neutral-black/35"
              />
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div className="flex min-w-0 flex-col gap-4">
                <p id={fromHeadingId} className="px-8 text-14 text-neutral-grey">
                  From
                </p>
                <div
                  ref={fromListRef}
                  role="listbox"
                  aria-labelledby={fromHeadingId}
                  tabIndex={0}
                  onFocus={() => setActiveColumn("from")}
                  onKeyDown={handleColumnKeyDown}
                  className="flex max-h-280 flex-col gap-4 overflow-y-auto outline-none"
                >
                  {filteredFrom.map((option, index) => {
                    const isSelected = option.value === pendingStart;

                    return (
                      <div
                        key={option.value}
                        role="option"
                        aria-selected={isSelected}
                        onMouseEnter={() => setFromHighlight(index)}
                        onClick={() => selectFrom(option.value)}
                        className={cn(
                          "flex cursor-pointer items-center gap-6 rounded-small px-16 py-12 text-left text-16 text-neutral-black transition-colors duration-100",
                          index === fromHighlight && "bg-tone-aqua-tint",
                        )}
                      >
                        <span className="flex size-16 shrink-0 items-center justify-center">
                          {isSelected && <CheckIcon className="size-16 text-neutral-black" />}
                        </span>
                        {option.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex min-w-0 flex-col gap-4">
                <p id={toHeadingId} className="px-8 text-14 text-neutral-grey">
                  To
                </p>
                {pendingStart ? (
                  <div
                    ref={toListRef}
                    role="listbox"
                    aria-labelledby={toHeadingId}
                    tabIndex={0}
                    onFocus={() => setActiveColumn("to")}
                    onKeyDown={handleColumnKeyDown}
                    className="flex max-h-280 flex-col gap-4 overflow-y-auto outline-none"
                  >
                    {filteredTo.map((option, index) => {
                      const isSelected = option.value === value?.end;

                      return (
                        <div
                          key={option.value}
                          role="option"
                          aria-selected={isSelected}
                          onMouseEnter={() => setToHighlight(index)}
                          onClick={() => selectTo(option.value)}
                          className={cn(
                            "flex cursor-pointer items-center gap-6 rounded-small px-16 py-12 text-left text-16 text-neutral-black transition-colors duration-100",
                            index === toHighlight && "bg-tone-aqua-tint",
                          )}
                        >
                          <span className="flex size-16 shrink-0 items-center justify-center">
                            {isSelected && <CheckIcon className="size-16 text-neutral-black" />}
                          </span>
                          <span className="min-w-0 flex-1">{option.label}</span>
                          <span className="text-14 text-neutral-grey">{formatDuration(pendingStart, option.value)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="px-16 py-12 text-16 text-neutral-black/35">Pick a start time first</p>
                )}
              </div>
            </div>

            <div className="border-t border-neutral-light-grey pt-8">
              <button
                type="button"
                onClick={clear}
                className="w-full cursor-pointer rounded-small px-16 py-12 text-left text-16 text-neutral-black outline-none transition-colors duration-100 hover:bg-neutral-black/5"
              >
                Clear
              </button>
            </div>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
